import { makeStyles } from '@mui/styles';
import { Theme, alpha } from '@mui/material';

import {
  CS_FONT,
  CS_WEIGHT,
  callGroundSurface,
  csTokens,
  endRed,
  secondaryText,
} from 'components/CallScribe/callScribeTheme';

const useWindowStyles = makeStyles((theme: Theme) => ({
  window: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    width: '100%',
    minWidth: 0,
    background: csTokens(theme).bg,
    color: csTokens(theme).text,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minHeight: 52,
    padding: theme.spacing(0, 0.5, 0, 1.5),
    borderBottom: `1px solid ${csTokens(theme).line}`,
    flex: '0 0 auto',
    color: csTokens(theme).text,
    '& .MuiIconButton-root': {
      width: 44,
      height: 44,
      borderRadius: 12,
      color: csTokens(theme).text2,
      '&.Mui-focusVisible': {
        outline: `2px solid ${csTokens(theme).focus}`,
        outlineOffset: -2,
      },
    },
  },
  headerMark: {
    display: 'flex',
    color: csTokens(theme).brand,
  },
  headerTitles: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flex: 1,
  },
  headerTitle: {
    ...CS_FONT.body,
    fontWeight: CS_WEIGHT.strong,
    color: csTokens(theme).text,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  headerSubtitle: {
    ...CS_FONT.caption,
    color: csTokens(theme).text2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  liveBar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minHeight: 60,
    padding: theme.spacing(0, 1, 0, 1.5),
    flex: '0 0 auto',
    minWidth: 0,
    background: csTokens(theme).callSoft,
    borderBottom: `1px solid ${csTokens(theme).line}`,
    color: csTokens(theme).text,
    '& .MuiIconButton-root': {
      width: 44,
      height: 44,
      color: csTokens(theme).text2,
      '&.Mui-focusVisible': {
        outline: `2px solid ${csTokens(theme).focus}`,
        outlineOffset: 0,
      },
    },
  },
  liveDot: {
    flex: '0 0 auto',
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: csTokens(theme).callText,
    animation: '$livePulse 1600ms ease-in-out infinite',
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  },
  '@keyframes livePulse': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.35 },
    '100%': { opacity: 1 },
  },
  liveLabelZone: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minWidth: 0,
    minHeight: 44,
    flex: 1,
    border: 'none',
    borderRadius: 10,
    background: 'transparent',
    padding: theme.spacing(0, 0.5),
    textAlign: 'left',
    cursor: 'pointer',
    font: 'inherit',
    color: 'inherit',
    '&:focus-visible': {
      outline: `2px solid ${csTokens(theme).focus}`,
      outlineOffset: 0,
    },
  },
  liveWho: {
    ...CS_FONT.small,
    fontWeight: CS_WEIGHT.strong,
    color: csTokens(theme).text,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  liveTimer: {
    ...CS_FONT.caption,
    color: csTokens(theme).text2,
    fontVariantNumeric: 'tabular-nums',
  },
  liveActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flex: '0 0 auto',
  },
  endButton: {
    color: csTokens(theme).endInk,
    background: endRed(theme),
    '&.MuiIconButton-root': {
      color: csTokens(theme).endInk,
    },
    '&:hover': {
      background: endRed(theme),
      filter: 'brightness(1.06)',
    },
  },
  backToCall: {
    flex: '0 0 auto',
    '&.MuiButton-root': {
      minHeight: 44,
      padding: theme.spacing(0, 1.5),
      borderRadius: 10,
      textTransform: 'none',
      fontFamily: 'inherit',
      fontSize: 13,
      lineHeight: '18px',
      fontWeight: CS_WEIGHT.strong,
      color: csTokens(theme).callText,
      '&:hover': { background: alpha(csTokens(theme).callText, 0.08) },
      '&.Mui-focusVisible': {
        outline: `2px solid ${csTokens(theme).focus}`,
        outlineOffset: 0,
      },
    },
  },
  failureBlock: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    textAlign: 'center',
    '&[hidden]': {
      display: 'none',
    },
  },
  failureHint: {
    ...theme.typography.body2,
    color: secondaryText(theme),
  },
  failureCause: {
    margin: 0,
    maxWidth: 400,
    padding: theme.spacing(1.75, 2),
    borderRadius: 16,
    background: alpha(csTokens(theme).stageEnd, 0.14),
    boxShadow: `inset 0 0 0 1px ${alpha(csTokens(theme).stageEnd, 0.5)}`,
    ...CS_FONT.small,
    color: csTokens(theme).onGround,
    overflowWrap: 'anywhere',
  },
  onStage: {
    color: csTokens(theme).onGround,
    '& $failureHint': {
      ...CS_FONT.small,
      color: csTokens(theme).onGroundMuted,
      maxWidth: 400,
    },
    '& .MuiButton-root': {
      minHeight: 48,
      minWidth: 200,
      padding: theme.spacing(0, 3),
      borderRadius: 12,
      textTransform: 'none',
      fontFamily: 'inherit',
      fontSize: 15,
      lineHeight: '22px',
      fontWeight: CS_WEIGHT.strong,
      color: csTokens(theme).onGround,
      transition: 'background-color 150ms ease-out',
      '&:hover': { background: csTokens(theme).glass },
      '&.Mui-focusVisible': {
        outline: `2px solid ${csTokens(theme).stageFocus}`,
        outlineOffset: 2,
      },
    },
    '& .MuiButton-contained': {
      background: csTokens(theme).onGround,
      color: csTokens(theme).ground,
      boxShadow: 'none',
      '&:hover': { background: csTokens(theme).onGroundMuted, boxShadow: 'none' },
      '&.Mui-disabled': {
        background: csTokens(theme).glassStrong,
        color: csTokens(theme).onGroundMuted,
      },
    },
  },
  crashControls: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
    flexWrap: 'wrap',
  },
  crashTitle: {
    ...theme.typography.subtitle1,
    color: theme.palette.text.primary,
  },
  callScreen: {
    ...callGroundSurface(theme),
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    padding: theme.spacing(2),
    boxSizing: 'border-box',
    overflow: 'hidden',
    position: 'relative',
    '&[hidden]': {
      display: 'none',
    },
  },
  headerOnGround: {
    color: csTokens(theme).onGround,
    background: csTokens(theme).ground,
    borderBottom: `1px solid ${csTokens(theme).glassBorder}`,
    '& $headerTitle': {
      color: csTokens(theme).onGround,
    },
    '& $headerSubtitle': {
      color: csTokens(theme).onGroundMuted,
    },
    '& $headerMark': {
      color: csTokens(theme).onGround,
    },
    '& .MuiIconButton-root': {
      color: csTokens(theme).onGround,
      '&:hover': { background: csTokens(theme).glass },
      '&.Mui-focusVisible': {
        outline: `2px solid ${csTokens(theme).stageFocus}`,
        outlineOffset: -2,
      },
    },
  },
  softphoneBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    minWidth: 0,
    flex: '0 0 auto',
    padding: theme.spacing(1, 1.5),
    background: alpha(endRed(theme), 0.1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& > span': {
      ...theme.typography.body2,
      color: theme.palette.text.primary,
      minWidth: 0,
    },
    '& button': {
      textTransform: 'none',
      flex: '0 0 auto',
    },
  },
  screens: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  screen: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
    '&[hidden]': {
      display: 'none',
    },
  },
}));

export default useWindowStyles;
